// src/shared/game/npc/shadow-stalker.ts — Plan 5

import type { GameState } from '../types';
import type { Player } from '../types';
import type { ShadowStalkerNpc } from './types';
import { appendEvent } from '../event-log';
import { despawnNpc, removeAllNpcsOfKind } from './registry';
import { applyDamage } from '../damage';
import { computeStalkerPath } from './pathfinding';
import type { BoardGraph } from '@shared/board/graph';
import type { DistanceCache } from '@shared/board/distance-cache';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

/** Steps per tick. */
export const SHADOW_STALKER_STEPS_PER_TICK = 4;

interface ShadowStalkerTarget {
  player: Player;
  distance: number;
  turnOrderIndex: number;
}

/**
 * Spawn a Shadow Stalker NPC summoned by `summonerId`. Enforces single-stalker invariant
 * (spec §4 rule 4) by despawning ANY existing stalker first.
 *
 * Spawn position: the summoner's current node (state.players.get(summonerId).nodeId).
 *
 * Emits: 1 NpcDespawnedEvent per old stalker removed (reason: 'replaced_by_new_stalker'),
 *        then 1 NpcSpawnedEvent for the new stalker.
 *
 * @returns the newly spawned ShadowStalkerNpc.
 * @throws Error if summonerId is not in state.players.
 */
export function spawnShadowStalker(state: GameState, summonerId: string): ShadowStalkerNpc {
  const summoner = state.players.get(summonerId);
  if (!summoner) {
    throw new Error(`spawnShadowStalker: summoner not found: ${summonerId}`);
  }

  // Enforce single-stalker invariant (spec §4 rule 4)
  removeAllNpcsOfKind(state, 'shadow_stalker', 'replaced_by_new_stalker');

  // Build the NPC id using state.eventLog.length (monotonically increasing, prevents
  // same-round same-summoner collision — Reviewer note 2).
  const npc: ShadowStalkerNpc = {
    id: `npc_shadow_stalker_${state.round}_${state.eventLog.length}_${summonerId}`,
    kind: 'shadow_stalker',
    nodeId: summoner.nodeId,
    spawnedAtRound: state.round,
    summonerId,
  };

  state.npcs.push(npc);

  appendEvent(state, {
    type: 'npc_spawned',
    playerId: summonerId,
    npcId: npc.id,
    npcKind: 'shadow_stalker',
    nodeId: npc.nodeId,
    summonerId,
  });

  return npc;
}

/**
 * Find the nearest player this stalker may hunt.
 */
export function findNearestShadowStalkerVictim(
  state: GameState,
  npc: ShadowStalkerNpc,
  distanceCache: DistanceCache,
): Player | null {
  let best: ShadowStalkerTarget | null = null;

  for (const player of state.players.values()) {
    if (!isEligibleShadowStalkerVictim(player, npc)) continue;

    const distance = distanceCache.getDistance(npc.nodeId, player.nodeId);
    if (distance < 0) continue;

    const turnOrderIndexRaw = state.turnOrder.indexOf(player.id);
    const turnOrderIndex = turnOrderIndexRaw === -1 ? Number.MAX_SAFE_INTEGER : turnOrderIndexRaw;
    if (
      best == null ||
      distance < best.distance ||
      (distance === best.distance && turnOrderIndex < best.turnOrderIndex)
    ) {
      best = { player, distance, turnOrderIndex };
    }
  }

  return best?.player ?? null;
}

function isEligibleShadowStalkerVictim(player: Player, npc: ShadowStalkerNpc): boolean {
  if (player.id === npc.summonerId) return false;
  if (player.besideGraveyardId !== null) return false;
  if (player.isDead) return false;
  return true;
}

function findVictimOnNode(state: GameState, npc: ShadowStalkerNpc, nodeId: string): Player | null {
  let best: { player: Player; turnOrderIndex: number } | null = null;
  for (const player of state.players.values()) {
    if (player.nodeId !== nodeId) continue;
    if (!isEligibleShadowStalkerVictim(player, npc)) continue;
    const turnOrderIndexRaw = state.turnOrder.indexOf(player.id);
    const turnOrderIndex = turnOrderIndexRaw === -1 ? Number.MAX_SAFE_INTEGER : turnOrderIndexRaw;
    if (best == null || turnOrderIndex < best.turnOrderIndex) {
      best = { player, turnOrderIndex };
    }
  }
  return best?.player ?? null;
}

function killVictimAndDespawn(state: GameState, npc: ShadowStalkerNpc, player: Player): void {
  if (
    tryBlockWithStoneSkin(state, player.id, {
      actorId: npc.id,
      sourceId: 'shadow_stalker',
    })
  ) {
    return;
  }

  appendEvent(state, {
    type: 'npc_killed_player',
    playerId: player.id,
    npcId: npc.id,
    npcKind: 'shadow_stalker',
    atNodeId: npc.nodeId,
  });
  const result = applyDamage(state, player.id, 999, 'npc_shadow_stalker', null);
  if (result.died) {
    despawnNpc(state, npc.id, 'killed_player');
  }
}

/**
 * Run one Shadow Stalker tick.
 *
 * Behavior (spec §4 rules 1, 2, 3, 7):
 *   1. Compute target = nearest eligible reachable player.
 *      If null, the stalker does NOT move and does NOT emit any event.
 *   2. Compute path = computeStalkerPath(graph, npc.nodeId, target, 4).
 *   3. For each step in path:
 *        a. Move the stalker (mutate npc.nodeId; emit NpcMovedEvent).
 *        b. Find any player whose nodeId === new node AND id !== summonerId AND
 *           besideGraveyardId === null AND isDead === false.
 *        c. If found: Stone Skin may block before kill/death/despawn events. Either way,
 *           STOP after that first attack attempt (rule 3 — one attack per tick).
 *
 * Notes:
 *   - Rule 1 is enforced by the `id !== summonerId` filter. Rule 1 is permanent for this
 *     stalker instance (never times out).
 *   - Rule 2 is enforced by the `besideGraveyardId === null` filter.
 *   - Rule 6 (persists across summoner death): NOT implemented here as a positive action; it
 *     is the ABSENCE of a despawn-on-summoner-death code path. Asserted by Task 7.2 integration test.
 *   - Pathing tiles (passthrough) are still walked; they ARE included in the path returned by
 *     computeStalkerPath. We emit one NpcMovedEvent per node entered (including pathing tiles).
 *     Players cannot stand on pathing tiles (per spec §3), so the kill-check is a no-op there.
 */
export function runShadowStalkerTick(
  state: GameState,
  npc: ShadowStalkerNpc,
  graph: BoardGraph,
  distanceCache: DistanceCache,
): void {
  const target = findNearestShadowStalkerVictim(state, npc, distanceCache);
  if (target === null) return;

  if (target.nodeId === npc.nodeId) {
    killVictimAndDespawn(state, npc, target);
    return;
  }

  const path = computeStalkerPath(graph, npc.nodeId, target.nodeId, SHADOW_STALKER_STEPS_PER_TICK);
  let stepIndex = 0;

  for (const nextNodeId of path) {
    const fromNodeId = npc.nodeId;
    npc.nodeId = nextNodeId;
    appendEvent(state, {
      type: 'npc_moved',
      playerId: null,
      npcId: npc.id,
      fromNodeId,
      toNodeId: nextNodeId,
      stepIndex,
      totalSteps: SHADOW_STALKER_STEPS_PER_TICK,
    });
    stepIndex += 1;

    // Check for eligible victim on the new tile
    const victim = findVictimOnNode(state, npc, nextNodeId);
    if (victim != null) {
      killVictimAndDespawn(state, npc, victim);
      return; // rule 3: stop after one kill
    }
  }
}
